import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { LAYER_META } from "./LayerBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { GaugeCircle } from "lucide-react";
import { toast } from "sonner";

const LEVEL_STYLES = {
  observe: "text-[#52525b]",
  recommend: "text-[#b45309]",
  copilot: "text-[#00579e]",
  autopilot: "text-[#15803d]",
};

export default function TrustPolicyPanel() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.autofix.trustPolicy().then(setPolicy).catch(console.error);
  }, []);

  const setLevel = async (layer, level) => {
    setSaving(true);
    try {
      const updated = await api.autofix.setTrustPolicy({ ...policy.levels, [layer]: level });
      setPolicy(updated);
      toast.success(`Layer ${layer} trust set to ${level}`, {
        description: updated.descriptions?.[level],
      });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!policy) return null;

  return (
    <div className="cs-card p-5" data-testid="trust-policy-panel">
      <div className="flex items-center gap-2 mb-1">
        <GaugeCircle className="w-4 h-4 text-[#00579e]" />
        <h2 className="cs-heading text-base font-semibold">Progressive Trust — AutoFix Execution Levels</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Control how much autonomy ASTRA has per waste layer: <span className="cs-mono">observe → recommend → co-pilot → autopilot</span>.
        Execution requests are gated against these levels with a full audit trail.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {[1, 2, 3, 4, 5].map((layer) => {
          const level = policy.levels?.[String(layer)] || "copilot";
          return (
            <div key={layer} className="border border-border rounded-sm p-3 border-t-4" style={{ borderTopColor: LAYER_META[layer]?.color }} data-testid={`trust-layer-${layer}`}>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-0.5">Layer {layer}</div>
              <div className="text-xs font-semibold mb-2">{LAYER_META[layer]?.name}</div>
              <Select value={level} onValueChange={(v) => setLevel(String(layer), v)} disabled={saving}>
                <SelectTrigger className={`rounded-sm h-8 text-xs cs-mono uppercase font-semibold ${LEVEL_STYLES[level]}`} data-testid={`trust-select-${layer}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(policy.available_levels || []).map((l) => (
                    <SelectItem key={l} value={l} data-testid={`trust-option-${layer}-${l}`}>
                      <span className={`cs-mono text-xs uppercase font-semibold ${LEVEL_STYLES[l]}`}>{l}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-2 leading-snug">
                {policy.descriptions?.[level]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
